import React, { useCallback } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import Feature from './feature/Feature';
import { ReactComponent as ChevronDownIcon } from '../../../assets/ic_chevron_down.svg';
import useTenantStore, { selectTenantId } from '../../../stores/TenantStore';
import OpenAPI from '../../../endpoint/Openapi';
import { type paths } from '../../../endpoint/Schema';
import InsightFeature from './insight/InsightFeature';
import { Icons } from '../../../assets/icons';
import FAQFeature from './faq/FAQFeature';
import DClubFeature from './DClub/DClubFeature';
import RewardFeature from './Reward/RewardFeature';
import LanguageFeature from './language/LanguageFeature';
import ReferralFeature from './referral/ReferralFeature';
import FlexpageFeature from './flexpage/FlexpageFeature';
import { type CustomError } from '../../../models/Error';

function Features() {
  const tenantId = useTenantStore(selectTenantId);
  const [insightScores, setInsightScores] = React.useState([60, 75]);
  const [insightSafeScoreColor, setInsightSafeScoreColor] =
    React.useState('#047B84');
  const [insightAlertScoreColor, setInsightAlertScoreColor] =
    React.useState('#F5E214');
  const [insightDangerScoreColor, setInsightDangerScoreColor] =
    React.useState('#D6190B');
  const [featureSettings, setFeatureSettings] = React.useState<
    | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
    | undefined
  >({});
  const [pending, setPending] = React.useState(false);
  const toast = React.useRef<Toast>(null);
  const allColoursList = React.useRef<
    paths['/api/v2/admin/tenant/app-color']['get']['responses']['200']['content']['application/json']
  >([]);

  const fetchSettings = useCallback(async () => {
    if (!tenantId) return;

    try {
      const res = await OpenAPI.GET('/api/v2/admin/feature-setting', {
        params: {
          query: {
            tenantId,
          },
        },
      });

      if (res) {
        setFeatureSettings(res.data);
        setInsightScores([
          res.data?.insightSettings?.scoreSettings?.alertState?.from ?? 60,
          res.data?.insightSettings?.scoreSettings?.alertState?.to ?? 75,
        ]);
      }
    } catch {}

    try {
      const res = await OpenAPI.GET('/api/v2/admin/tenant/app-color', {
        params: { query: { tenantId } },
      });

      if (res.data && res.data.length > 0) {
        allColoursList.current = res.data;
        allColoursList.current = allColoursList.current.filter(
          (item) =>
            item.type !== 'LSf' && item.type !== 'Al' && item.type !== 'Dn',
        );

        let color = res.data.find((item) => item.type === 'LSf')?.value;
        if (color) setInsightSafeScoreColor(color);
        color = res.data.find((item) => item.type === 'Al')?.value;
        if (color) setInsightAlertScoreColor(color);
        color = res.data.find((item) => item.type === 'Dn')?.value;
        if (color) setInsightDangerScoreColor(color);
      }
    } catch {}
  }, [tenantId]);

  React.useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  async function saveSettings() {
    if (!tenantId) return;

    const settings = { ...featureSettings };
    settings.insightSettings = {
      ...settings.insightSettings,
      scoreSettings: {
        ...settings?.insightSettings?.scoreSettings,
        safeState: {
          from: insightScores[1],
          to: 100,
        },
        alertState: {
          from: insightScores[0],
          to: insightScores[1],
        },
        dangerState: {
          from: 0,
          to: insightScores[0],
        },
      },
    };

    if (
      settings?.insightSettings?.scoreSettings?.safeState &&
      settings?.insightSettings?.scoreSettings?.alertState &&
      (settings?.insightSettings?.scoreSettings.safeState.from ?? 0) <
        (settings?.insightSettings?.scoreSettings.alertState.from ?? 0)
    ) {
      toast.current?.show({
        severity: 'error',
        summary: 'Insight settings error',
        detail:
          'In the Insight settings, the Alert state from must be less than the Safe state from',
        life: 3000,
      });
      return;
    }

    try {
      setPending(true);

      const res = await OpenAPI.POST('/api/v2/admin/feature-setting', {
        params: {
          query: {
            tenantId,
          },
        },
        body:
          settings?.insightSettings &&
          settings.insightSettings.insightSettingsText?.length === 0
            ? {
                ...settings,
                insightSettings: {
                  ...settings.insightSettings,
                  insightSettingsText: undefined,
                },
              }
            : settings,
      });
      // @ts-expect-error
      if (res.error) throw res.error;

      await OpenAPI.PUT('/api/v2/admin/tenant/app-color', {
        params: { query: { tenantId } },
        body: [
          ...allColoursList.current,
          {
            type: 'LSf',
            value: insightSafeScoreColor,
            displayName: 'Light Safe',
          },
          {
            type: 'Al',
            value: insightAlertScoreColor,
            displayName: 'Alert',
          },
          {
            type: 'Dn',
            value: insightDangerScoreColor,
            displayName: 'Danger',
          },
        ],
      });

      toast.current?.show({
        severity: 'success',
        summary: `Saved successfully`,
        life: 3000,
      });

      if (res) {
        setFeatureSettings(res.data);
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setPending(false);
  }

  function handleTripsChecked(checked: boolean) {
    setFeatureSettings((prev) => ({
      ...prev,
      tripSettings: { ...prev?.tripSettings, enabled: checked },
    }));
  }

  return (
    <div className="flex flex-col">
      <div className="flex gap-5 items-end px-5 text-lg font-medium max-md:flex-wrap">
        <div className="flex-auto mt-9 text-black leading-[156%] max-md:max-w-full">
          Enable or disble every feature you want in your app.
        </div>
        <div className="flex gap-5 justify-between">
          <button
            className="flex gap-2 justify-between items-center px-3 py-1 text-black rounded-2xl border border-solid border-stone-300 hover:bg-whiteSmoke"
            onClick={fetchSettings}
          >
            <div>Discard</div>
            <ChevronDownIcon className="shrink-0 my-auto w-4 aspect-[2] stroke-[2px] stroke-neutral-400" />
          </button>
          <div className="flex gap-2.5 rounded-2xl border border-solid border-stone-300">
            {pending ? (
              <ProgressSpinner className="w-8 h-8 my-auto mx-6" />
            ) : (
              <button
                className="flex self-stretch justify-center items-center px-5 py-1 bg-primary hover:bg-primaryDark text-white rounded-2xl"
                onClick={saveSettings}
                disabled={pending}
              >
                <div className="text-white">Save</div>
              </button>
            )}
            <ChevronDownIcon className="shrink-0 my-auto w-4 aspect-[2] stroke-[2px] stroke-neutral-400" />
            <div className="w-2" />
          </div>
        </div>
      </div>

      <Feature
        icon={Icons.ic_car}
        image="img_dashboard"
        title="Dashboard"
        description="This feature is designed to provide users with a comprehensive view of their driving performance, including a detailed driving score and its subcategories such as speeding, cornering, braking, maneuvering, and phone usage. Users can easily compare their current scores with those from the previous week, empowering them to track their progress and identify areas for improvement."
        isMandatory
        checked
      />
      <InsightFeature
        featureSettings={featureSettings}
        setFeatureSettings={setFeatureSettings}
        insightScores={insightScores}
        setInsightScores={setInsightScores}
        insightSafeScoreColor={insightSafeScoreColor}
        setInsightSafeScoreColor={setInsightSafeScoreColor}
        insightAlertScoreColor={insightAlertScoreColor}
        setInsightAlertScoreColor={setInsightAlertScoreColor}
        insightDangerScoreColor={insightDangerScoreColor}
        setInsightDangerScoreColor={setInsightDangerScoreColor}
      />
      <RewardFeature
        featureSettings={featureSettings}
        setFeatureSettings={setFeatureSettings}
      />
      <DClubFeature
        featureSettings={featureSettings}
        setFeatureSettings={setFeatureSettings}
      />
      <Feature
        icon={Icons.ic_trips}
        image="img_trips"
        title="Trips"
        description="The Trips feature enables users to review detailed information about their past trips. Users can easily access trip details such as distance traveled, duration, route taken, and any notable events or stops along the way. This feature provides users with valuable insights into their travel history, facilitating reflection and analysis of their driving habits and experiences."
        checked={featureSettings?.tripSettings?.enabled ?? false}
        onChange={handleTripsChecked}
      />
      <FAQFeature
        featureSettings={featureSettings}
        setFeatureSettings={setFeatureSettings}
      />
      <FlexpageFeature
        featureSettings={featureSettings}
        setFeatureSettings={setFeatureSettings}
      />
      <LanguageFeature
        featureSettings={featureSettings}
        setFeatureSettings={setFeatureSettings}
      />
      <ReferralFeature
        featureSettings={featureSettings}
        setFeatureSettings={setFeatureSettings}
      />
      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default Features;
